import { io, Socket } from 'socket.io-client';
import { create } from 'zustand';
import { ISocketState } from './types';
import type {
  TSocketListenMessageCamel,
  TSocketSendMessageCamel,
} from '@/types/socket';
import { convertKeys } from '@/utils/common';
import { LOCAL_STORAGE_KEYS } from '@/utils/constants';

const getToken = () => {
  const token = window.localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  if (!token) {
    console.error('No token found in localStorage');
  }
  return token ? `Bearer ${token}` : null;
};

const socket: Socket = io(import.meta.env.VITE_API_WEBSOCKET, {
  transports: ['websocket'],
  autoConnect: false,
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 20000,
});

export const useSocketStore = create<ISocketState>((set, get) => {
  // Restore all subscriptions after reconnect
  const restoreSubscriptions = () => {
    const { activeSubscriptions, activeListeners } = get();

    console.info('Restoring subscriptions after reconnect...');

    // Restore all listeners
    activeListeners.forEach((listener) => {
      // Remove old listener to prevent duplicates
      socket.off(listener.type);

      socket.on(listener.type, (message) => {
        const messageCamel = convertKeys(message);
        console.info(`${listener.type} Message:`, messageCamel);
        listener.callback(messageCamel);
      });
    });

    // Resend all subscription messages
    activeSubscriptions.forEach((subscription) => {
      console.info(`Resubscribing to ${subscription.type}...`);
      socket.emit(subscription.type, convertKeys(subscription.body, 'snake'));
    });
  };

  // Setup socket event handlers
  socket.on('connect', () => {
    console.info('Socket connected');
    set({ isConnected: true, hasConnectedOnce: true, isReconnecting: false });

    // Restore subscriptions on successful connection
    const { activeSubscriptions } = get();
    if (activeSubscriptions.size > 0) {
      restoreSubscriptions();
    }
  });

  socket.on('disconnect', (reason) => {
    console.warn('Socket disconnected:', reason);
    set({ isConnected: false });
  });

  socket.on('reconnect', (attemptNumber) => {
    console.info(`Socket reconnected after ${attemptNumber} attempts`);
    set({ isReconnecting: false });
  });

  socket.on('reconnect_attempt', (attemptNumber) => {
    console.info(`Socket reconnection attempt ${attemptNumber}...`);
    set({ isReconnecting: true });
  });

  socket.on('reconnect_error', (error) => {
    console.error('Socket reconnection error:', error);
  });

  socket.on('connect_error', (error) => {
    console.error('Socket connection error:', error);
    const { hasConnectedOnce } = get();
    // If we were connected before, this is a reconnection attempt
    if (hasConnectedOnce) {
      set({ isReconnecting: true });
    }
  });

  socket.on('reconnect_failed', () => {
    console.error('Socket reconnection failed');
  });

  return {
    isConnected: false,
    hasConnectedOnce: false,
    isReconnecting: false,
    activeSubscriptions: new Map(),
    activeListeners: new Map(),

    connectSocket: () => {
      const token = getToken();
      if (!token) return;

      socket.auth = {
        token,
      };

      if (socket.connected) return;
      socket.connect();
    },

    disconnectSocket: () => {
      const { activeSubscriptions, activeListeners } = get();

      // Clear all subscriptions
      activeSubscriptions.clear();
      activeListeners.clear();

      socket.disconnect();
      socket.close();
      set({
        isConnected: false,
        hasConnectedOnce: false,
        isReconnecting: false,
        activeSubscriptions: new Map(),
        activeListeners: new Map(),
      });
      console.info('Socket disconnected');
    },

    listenMessage: (msg: TSocketListenMessageCamel) => {
      const { activeListeners } = get();
      const { type, callback } = msg;
      // Save listener for restoration after reconnect
      activeListeners.set(type, msg);
      set({ activeListeners: new Map(activeListeners) });

      // Remove old listener to prevent duplicates
      socket.off(type);

      socket.on(type, (message) => {
        const messageCamel = convertKeys(message);
        console.info(`${type} Message:`, messageCamel);
        callback(messageCamel);
      });
    },

    stopListenMessage: (type: TSocketListenMessageCamel['type']) => {
      const { activeListeners } = get();

      // Remove listener from saved listeners
      activeListeners.delete(type);
      set({ activeListeners: new Map(activeListeners) });

      socket.off(type);
    },

    sendMessage: (msg: TSocketSendMessageCamel) => {
      const { activeSubscriptions } = get();
      const { type, body } = msg;

      // If this is a start subscription - save it for restoration
      if (type.endsWith(':start')) {
        activeSubscriptions.set(type, msg);
        set({ activeSubscriptions: new Map(activeSubscriptions) });
        console.info(`Saved subscription: ${type}`);
      }

      // If this is a stop subscription - remove it from saved subscriptions
      if (type.endsWith(':stop')) {
        const startType = type.replace(':stop', ':start');
        activeSubscriptions.delete(startType);
        set({ activeSubscriptions: new Map(activeSubscriptions) });
        console.info(`Removed subscription: ${startType}`);
      }

      socket.emit(type, convertKeys(body, 'snake'));
    },

    sendBinary: (msg) => {
      socket.emit(msg.type, msg.data);
    },

    sendRequest: async (msg) => {
      const { type, body } = msg;
      return await socket.emitWithAck(type, convertKeys(body, 'snake'));
    },
  };
});
